import {
  Grid,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useAppSelector } from "../../app/store/configureStore";
import { useForm } from "react-hook-form";
import { AnswersProps } from "../../app/models/Answers";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = yup.object().shape({
  distributed: yup
    .number()
    .typeError("Amount required")
    .required("Amount required"),
  collected: yup
    .number()
    .typeError("Amount required")
    .required("Amount required"),
  summaryA: yup.string().required("Summary required"),
  summaryB: yup.string().required("Summary required"),
});

const EFormTableQuestionnaire: React.FC<AnswersProps> = ({
  answersData,
  onAnswerChange,
}) => {
  const { user } = useAppSelector((state) => state.account);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const defaultValues = {
    distributed: answersData.find((answer) => answer.systemCodeId === 323)
      ?.answer,
    collected: answersData.find((answer) => answer.systemCodeId === 324)
      ?.answer,
    summaryA: answersData.find((answer) => answer.systemCodeId === 325)?.answer,
    summaryB: answersData.find((answer) => answer.systemCodeId === 326)?.answer,
  };

  const {
    register,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  return (
    <Grid container p={3}>
      <Grid item m={1}>
        <Typography fontSize={20} fontWeight={700}>
          Candidate
        </Typography>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={4}>
          <TextField
            defaultValue={user?.firstName}
            label="First Name"
            variant="filled"
            disabled
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            defaultValue={user?.lastName}
            label="Last Name"
            variant="filled"
            disabled
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            defaultValue={user?.cdaCouncilCandidateId}
            label="Candidate ID Number"
            variant="filled"
            disabled
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid item container p={1} m={1} spacing={3}>
        <Grid item mt={2}>
          <Typography fontSize={20} fontWeight={700}>
            Summary
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>
            <span style={{ fontWeight: 700 }}>1.</span> I distributed{" "}
            <TextField
              variant="standard"
              sx={{ width: 50 }}
              placeholder=" #"
              type="number"
              {...register("distributed")}
              value={
                answersData.find((answer) => answer.systemCodeId === 323)
                  ?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = 323;
                newValue.length <= 10 && onAnswerChange(systemCodeId, newValue);
              }}
              error={!!errors.distributed?.message}
              helperText={errors.distributed?.message}
            />
            Family Questionnaires.
          </Typography>
        </Grid>
        <Grid item xs={12} p={2} mt={1} bgcolor="myVerify.light">
          <Typography>
            <span style={{ fontWeight: 700 }}>2.</span> I collected{" "}
            <TextField
              variant="standard"
              sx={{ width: 50 }}
              type="number"
              placeholder=" #"
              {...register("collected")}
              value={
                answersData.find((answer) => answer.systemCodeId === 324)
                  ?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = 324;
                newValue.length <= 10 && onAnswerChange(systemCodeId, newValue);
              }}
              error={!!errors.collected?.message}
              helperText={errors.collected?.message}
            />{" "}
            Family Questionnaires and placed them behind this Summary sheet.
            Therefore, I collected the “majority” (more than half) of the
            Questionnaires I distributed.
          </Typography>
        </Grid>
        <Grid item xs={isMobile ? 12 : 8}>
          <Typography>
            <span style={{ fontWeight: 700 }}>3.</span> I have looked for
            patterns or trends of feedback from these Questionnaires. Upon
            reflection, I think some of the families see the following as my
            greatest professional strength(s) and area(s) for professional
            growth.
          </Typography>
        </Grid>
        <Grid item xs={isMobile ? 12 : 7} ml={isMobile ? 0 : 6}>
          <TextField
            label="Area(s) of Strength (list at least one)"
            multiline
            rows={3}
            fullWidth
            inputProps={{ maxLength: 500 }}
            {...register("summaryA")}
            value={
              answersData.find((answer) => answer.systemCodeId === 325)
                ?.answer || ""
            }
            onChange={(e) => {
              const newValue = e.target.value;
              const systemCodeId = 325;
              newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
            }}
            error={!!errors.summaryA?.message}
            helperText={errors.summaryA?.message}
          />
          <Typography fontSize={13}>
            {defaultValues.summaryA?.length}/500
          </Typography>
        </Grid>
        <Grid item xs={isMobile ? 12 : 7} ml={isMobile ? 0 : 6}>
          <TextField
            label="Area(s) for Professional Growth (list at least one)"
            inputProps={{ maxLength: 500 }}
            multiline
            rows={3}
            fullWidth
            {...register("summaryB")}
            value={
              answersData.find((answer) => answer.systemCodeId === 326)
                ?.answer || ""
            }
            onChange={(e) => {
              const newValue = e.target.value;
              const systemCodeId = 326;
              newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
            }}
            error={!!errors.summaryB?.message}
            helperText={errors.summaryB?.message}
          />
          <Typography fontSize={13}>
            {defaultValues.summaryB?.length}/500
          </Typography>
        </Grid>
        <Grid item xs={12} bgcolor="myVerify.light" p={2} mt={2}>
          <Typography>
            <span style={{ fontWeight: 700 }}>4.</span> I have taken the areas
            of strength and growth that I wrote, above, and entered them into
            Boxes A and B in my Reflective Dialogue Worksheet.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontWeight={700} fontSize={20}>
            Note to the CDA PD Specialist™:
          </Typography>
          <Typography>
            Please do not read the contents of the Family Questionnaires. This
            feedback is private, between this candidate and the families she/he
            serves. Simply count the Questionnaires and verify that the number
            of questionnaires matches the number written in #2, above. If the
            number matches, consider this required task complete. If the number
            does not match record this information in Item 13.4 of the
            Comprehensive Scoring Instrument.
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EFormTableQuestionnaire;
